import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, Sparkles, ArrowUp, ArrowDown, Minus, Factory, Server, RefreshCw, ExternalLink, Star,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine,
} from "recharts";
import { fetchIsmPmi, fetchIsmRead } from "@/lib/api";
import { tooltipStyle, tooltipLabelStyle } from "@/lib/chartTheme";

const fmt = (v) => (v == null ? "—" : Number(v).toFixed(1));

function Delta({ v }) {
  if (v == null) return <span className="text-zinc-700 tabular-nums text-[11px]">--</span>;
  const cls = v > 0 ? "text-pos" : v < 0 ? "text-neg" : "text-zinc-500";
  const Arrow = v > 0 ? ArrowUp : v < 0 ? ArrowDown : Minus;
  return (
    <span className={`inline-flex items-center gap-0.5 tabular-nums text-[11px] ${cls}`}>
      <Arrow size={10} strokeWidth={2.5} />{Math.abs(v).toFixed(1)}
    </span>
  );
}

function ReportPanel({ Icon, d }) {
  if (!d) return null;
  const color = d.expanding ? "#22c55e" : "#ef4444";
  return (
    <div className="border border-zinc-800 bg-[#09090B]" data-testid={`ism-panel-${d.name.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="px-3 py-2.5 border-b border-zinc-800 flex items-center justify-between gap-2">
        <span className="text-[11px] uppercase tracking-widest text-zinc-300 flex items-center gap-1.5">
          <Icon size={13} strokeWidth={1.5} className="text-amber-term" /> {d.name}
          {d.month && <span className="text-zinc-600 normal-case tracking-normal">· {d.month}</span>}
        </span>
        <span className="flex items-baseline gap-2.5">
          <span className={`text-2xl font-semibold tabular-nums leading-none ${d.expanding ? "text-pos" : "text-neg"}`}>{fmt(d.headline)}</span>
          <Delta v={d.change} />
          <span className="text-[10px] uppercase tracking-widest text-zinc-600">12M avg {fmt(d.twelve_month_avg)}</span>
          <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-zinc-700 ${d.expanding ? "text-pos" : "text-neg"}`}>
            {d.expanding ? "Expanding" : "Contracting"}
          </span>
        </span>
      </div>

      <div className="h-[240px] w-full px-2 pt-4">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={d.history} margin={{ top: 6, right: 16, left: -6, bottom: 0 }}>
            <CartesianGrid stroke="#18181B" vertical={false} />
            <XAxis dataKey="month" tickFormatter={(m) => String(m).split(" ")[0]} tick={{ fontSize: 10, fill: "#71717A", fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={{ stroke: "#27272A" }} minTickGap={12} />
            <YAxis domain={[45, 60]} ticks={[45, 50, 55, 60]} tick={{ fontSize: 10, fill: "#71717A", fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={{ stroke: "#27272A" }} width={34} />
            <Tooltip contentStyle={tooltipStyle} labelStyle={tooltipLabelStyle} formatter={(v) => [Number(v).toFixed(1), "PMI"]} />
            <ReferenceLine y={50} stroke="#52525B" strokeDasharray="4 3" label={{ value: "50 · breakeven", position: "insideBottomRight", fill: "#71717A", fontSize: 9 }} />
            <Line name="PMI" type="monotone" dataKey="value" stroke={color} strokeWidth={2} dot={{ r: 2.5, fill: color }} activeDot={{ r: 4 }} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="px-3 py-3 border-t border-zinc-900">
        <div className="text-[10px] uppercase tracking-widest text-zinc-600 mb-2 flex items-center gap-1.5">
          Sub-indexes · current vs prior month
          <span className="inline-flex items-center gap-1 text-amber-term">
            · <Star size={10} strokeWidth={1.5} fill="currentColor" /> key driver
          </span>
        </div>
        {/* column headers */}
        <div className="flex items-center gap-2 text-[9px] uppercase tracking-widest text-zinc-600 pb-1.5 border-b border-zinc-800">
          <span className="flex-1">Index</span>
          <span className="w-12 text-right">Prev</span>
          <span className="w-14 text-right">Current</span>
          <span className="w-14 text-right">Δ ppt</span>
          <span className="w-24 text-right hidden sm:block">Direction</span>
          <span className="w-28 text-right hidden sm:block">Rate of Change</span>
        </div>
        <div className="divide-y divide-zinc-900/70">
          {d.subindexes?.map((s) => {
            const delta = s.cur != null && s.prev != null ? Math.round((s.cur - s.prev) * 10) / 10 : null;
            const id = s.name.replace(/\s+/g, "-").toLowerCase();
            return (
              <div
                key={s.name}
                className={`flex items-center gap-2 text-[11px] py-1.5 ${s.important ? "bg-amber-500/[0.04]" : ""}`}
                data-testid={`ism-sub-${id}`}
              >
                <span className="flex-1 flex items-center gap-1.5 min-w-0">
                  {s.important && (
                    <Star size={11} strokeWidth={1.5} fill="currentColor" className="text-amber-term shrink-0" data-testid={`ism-star-${id}`} />
                  )}
                  <span className={`truncate ${s.important ? "text-zinc-100 font-medium" : "text-zinc-400"}`}>{s.name}</span>
                </span>
                <span className="w-12 text-right tabular-nums text-zinc-500">{fmt(s.prev)}</span>
                <span className={`w-14 text-right tabular-nums font-semibold ${s.cur >= 50 ? "text-pos" : "text-neg"}`}>{fmt(s.cur)}</span>
                <span className="w-14 text-right"><Delta v={delta} /></span>
                <span className="w-24 text-right text-[10px] uppercase tracking-wide text-zinc-500 hidden sm:block">{s.direction}</span>
                <span className="w-28 text-right text-[10px] uppercase tracking-wide text-zinc-400 hidden sm:block">{s.rate || "—"}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function ISMPage() {
  const qc = useQueryClient();
  const [refreshingRead, setRefreshingRead] = useState(false);
  const q = useQuery({ queryKey: ["ism-pmi"], queryFn: fetchIsmPmi, staleTime: 1000 * 60 * 60 * 6 });
  const readQ = useQuery({ queryKey: ["ism-read"], queryFn: () => fetchIsmRead(false), staleTime: 1000 * 60 * 60 * 12, retry: false });
  const d = q.data;

  const refreshRead = async () => {
    setRefreshingRead(true);
    try {
      qc.setQueryData(["ism-read"], await fetchIsmRead(true));
    } catch {
      /* keep prior read */
    } finally {
      setRefreshingRead(false);
    }
  };
  const readBusy = readQ.isFetching || refreshingRead;

  return (
    <div className="min-h-screen bg-black text-zinc-100" data-testid="ism-page">
      <div className="border-b border-zinc-800 px-4 py-2.5 flex items-center gap-3 sticky top-0 bg-black z-10">
        <Link to="/" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-zinc-500 hover:text-amber-term transition-colors" data-testid="ism-back">
          <ArrowLeft size={13} strokeWidth={1.5} /> Terminal
        </Link>
        <span className="text-zinc-700">/</span>
        <span className="text-[13px] font-semibold text-zinc-100">ISM PMI Monitor</span>
        <span className="text-[11px] uppercase tracking-widest text-zinc-600 ml-auto hidden sm:inline">
          Manufacturing · Services · {d?.as_of_month || ""}
        </span>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-5 space-y-5">
        {/* AI Read */}
        <div className="border border-amber-500/30 bg-[#09090B]" data-testid="ism-ai-read">
          <div className="px-4 py-2 border-b border-amber-500/20 flex items-center gap-2">
            <Sparkles size={13} strokeWidth={1.5} className="text-amber-term" />
            <span className="text-[11px] uppercase tracking-widest text-zinc-300">ISM Read · Claude Sonnet 4.5 · Growth × Prices × USD</span>
            {readQ.data?.signal && (
              <span className="ml-auto text-[11px] uppercase tracking-widest text-amber-term border border-amber-500/40 px-2 py-0.5" data-testid="ism-signal">
                {readQ.data.signal}
              </span>
            )}
            <button
              onClick={refreshRead}
              disabled={readBusy}
              data-testid="ism-read-refresh"
              title="Regenerate the AI read"
              className={`${readQ.data?.signal ? "" : "ml-auto"} text-zinc-500 hover:text-amber-term transition-colors disabled:opacity-50`}
            >
              <RefreshCw size={12} strokeWidth={1.5} className={readBusy ? "animate-spin" : ""} />
            </button>
          </div>
          <div className="px-4 py-3">
            {readQ.isLoading ? (
              <p className="text-sm text-zinc-500 animate-pulse">Reading the US growth pulse from ISM Manufacturing & Services…</p>
            ) : readQ.isError ? (
              <p className="text-sm text-zinc-600">AI read unavailable right now — the underlying prints below are still live.</p>
            ) : (
              <p className="text-sm text-zinc-100 leading-relaxed" data-testid="ism-read-text">{readQ.data?.read}</p>
            )}
          </div>
        </div>

        {/* Report panels — stacked vertically for full-width sub-index tables */}
        <div className="space-y-4">
          {d ? (
            <>
              <ReportPanel Icon={Factory} d={d.manufacturing} />
              <ReportPanel Icon={Server} d={d.services} />
            </>
          ) : (
            Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="border border-zinc-800 bg-[#09090B] h-[460px] animate-pulse" data-testid={`ism-panel-skeleton-${i}`} />
            ))
          )}
        </div>

        <div className="flex items-center justify-between gap-2 text-[10px] uppercase tracking-widest text-zinc-600">
          <span>A reading above 50 signals expansion · dashed line = 50 breakeven · Manufacturing releases 1st business day, Services 3rd</span>
          <a href={d?.manufacturing?.url} target="_blank" rel="noreferrer" data-testid="ism-source-link" className="flex items-center gap-1 hover:text-amber-term transition-colors shrink-0">
            Source: ISM <ExternalLink size={10} />
          </a>
        </div>
      </div>
    </div>
  );
}
